import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { FormBase } from 'src/app/_models/form-base';
import { Router } from '@angular/router';
import { ErrorObject } from 'src/app/_models/error';
import { CalibrationService } from 'src/app/_services/calibration.service';
import { DeviceService } from 'src/app/_services/device.service';
import {
  Calibration,
  CalibrationSlot,
  ManualCalibration,
} from 'src/app/_models/calibration';
import { OxygenCalibration } from 'src/app/_models/oxygen-calibration';
import { Device } from 'src/app/_models/device';
import { DeviceIndexResponse } from 'src/app/_models/api-responses';
import { Module } from 'src/app/_models/module-enum';
import { PhCalibration } from 'src/app/_models/ph-calibration';
import { DeviceSetupStatus } from 'src/app/_models/device-setup-status';
import { advancedNameRegex, pHBufferOptions } from 'src/app/_helpers/utils';
import { AuthenticationService } from 'src/app/_services/authentication.service';
import { Laboratory } from 'src/app/_models/laboratory';
import { ErrorPageComponent } from '../../_components/error-page/error-page.component';
import { ButtonComponent } from '../../_components/buttons/button/button.component';
import { SlideToggleComponent } from '../../_components/selection-controls/slide-toggle/slide-toggle.component';
import { UnderlinedFormFieldComponent } from '../../_components/forms/underlined-form-field/underlined-form-field.component';
import { SmallLoadingSpinnerComponent } from '../../_components/small-loading-spinner/small-loading-spinner.component';
import { UnderlinedDropdownFormFieldComponent } from '../../_components/forms/underlined-dropdown-form-field/underlined-dropdown-form-field.component';
import { ChipRadioButtonComponent } from '../../_components/selection-controls/chip-radio-button/chip-radio-button.component';
import { Buffer } from 'src/app/_models/buffer';
import { BufferValidator } from 'src/app/_helpers/ph-buffer.validator';
import { SpargingCalibration } from 'src/app/_models/sparging-calibration';
import { NumberFormFieldComponent } from 'src/app/_components/forms/number-form-field/number-form-field.component';
import { Subject, Subscription, take, takeUntil } from 'rxjs';
import { AuthEventService } from 'src/app/_services/auth-event.service';
import { User } from 'src/app/_models/user';
import { ModalComponent } from 'src/app/_components/modals/modal.component';
import { ModalService } from 'src/app/_services/modal.service';
import { NotesParameterRanges } from 'src/app/_helpers/notes-min-max';
import { UnderlinedTextareaFieldComponent } from 'src/app/_components/forms/underlined-textarea-field/underlined-textarea-field.component';
import { AltOptionTabToggleComponent } from 'src/app/_components/selection-controls/alt-option-tab-toggle/alt-option-tab-toggle.component';
import { TabOptions } from 'src/app/_models/tab-options';
import { CalibrationParameterRanges } from 'src/app/_helpers/calibration-min-max';

interface BufferOption {
  key: string;
  value: Buffer;
}
// TODO
// 2. Add warning if user selects a slot that already has a calibration

@Component({
  selector: 'app-create-calibration',
  templateUrl: './create-calibration.component.html',
  styleUrls: ['./create-calibration.component.scss'],
  imports: [
    ChipRadioButtonComponent,
    UnderlinedDropdownFormFieldComponent,
    SmallLoadingSpinnerComponent,
    UnderlinedFormFieldComponent,
    SlideToggleComponent,
    ButtonComponent,
    ErrorPageComponent,
    NumberFormFieldComponent,
    ModalComponent,
    UnderlinedTextareaFieldComponent,
    AltOptionTabToggleComponent,
  ],
})
export class CreateCalibrationComponent implements OnInit, OnDestroy {
  protected readonly formBuilder = inject(FormBuilder);
  protected readonly calibrationService = inject(CalibrationService);
  protected readonly deviceService = inject(DeviceService);
  private readonly authenticationService = inject(AuthenticationService);
  protected readonly router = inject(Router);
  private readonly authEventService = inject(AuthEventService);
  private readonly modalService = inject(ModalService);

  allowNavigation: Subject<boolean> = new Subject<boolean>();
  private readonly unsubscribe$ = new Subject<void>();

  // Permissions
  laboratory: Laboratory;
  currentUser: User;

  // Control vars
  private deviceSubscription?: Subscription;
  private setupSubscription?: Subscription;
  private calibrationTypeSubscription?: Subscription;
  private deviceSelectionSubscription?: Subscription;
  private temperatureControlSubscriptions?: Subscription[] = [];
  private pHBufferSubscriptions?: Subscription[] = [];

  error: ErrorObject = null;
  navOptions: TabOptions[];
  selectedTabOption = '100';
  loading = false;
  devicesLoading = true;
  saveLocationsLoading = false;
  isProcessing = false;
  noSlotsWarning = false;
  displayFormFields = true;
  isManualCalibration = true;
  // temperature vars - used for OD and Oxygen calibrations
  minTemperatureODAndOxygen = 21;
  maxTemperatureODAndOxygen = 50;
  defaultTempValue = 37;
  temperatureStepOD = 0.1;
  // pH calibration vars
  // if controlTemperature is true maxTemperaturePH should be set to 50
  controlTemperature = false;
  minTemperaturePH = 15;
  maxTemperaturePH = 100;
  tempStep = 0.1;
  buffersLoading = false;
  // sparging calibration vars
  volumeStep = 1;
  minVolume = 50;
  maxVolume = 400;

  // Manual calibration parameters
  odOffsetMin = CalibrationParameterRanges.OD_OFFSET.min;
  odOffsetMax = CalibrationParameterRanges.OD_OFFSET.max;
  odOffsetStep = CalibrationParameterRanges.OD_OFFSET.step;
  odBelowRangeWarning = CalibrationParameterRanges.OD_OFFSET.belowRangeWarning;
  odBelowRangeWarningMessage =
    CalibrationParameterRanges.OD_OFFSET.warningMessage;
  odFactorMin = CalibrationParameterRanges.OD_FACTOR.min;
  odFactorMax = CalibrationParameterRanges.OD_FACTOR.max;
  odFactorStep = CalibrationParameterRanges.OD_FACTOR.step;
  odPowerMin = CalibrationParameterRanges.OD_POWER.min;
  odPowerMax = CalibrationParameterRanges.OD_POWER.max;
  odPowerStep = CalibrationParameterRanges.OD_POWER.step;
  odPowerAboveRangeWarning =
    CalibrationParameterRanges.OD_POWER.aboveRangeWarning;
  odPowerAboveRangeWarningMessage =
    CalibrationParameterRanges.OD_POWER.warningMessage;
  odTemperatureMin = CalibrationParameterRanges.OD_TEMPERATURE.min;
  odTemperatureMax = CalibrationParameterRanges.OD_TEMPERATURE.max;
  odTemperatureStep = CalibrationParameterRanges.OD_TEMPERATURE.step;

  oxygenDphi0Min = CalibrationParameterRanges.OXYGEN_DPHI_0.min;
  oxygenDphi0Max = CalibrationParameterRanges.OXYGEN_DPHI_0.max;
  oxygenDphi0Step = CalibrationParameterRanges.OXYGEN_DPHI_0.step;
  oxygenDphi100Min = CalibrationParameterRanges.OXYGEN_DPHI_100.min;
  oxygenDphi100Max = CalibrationParameterRanges.OXYGEN_DPHI_100.max;
  oxygenDphi100Step = CalibrationParameterRanges.OXYGEN_DPHI_100.step;
  oxygenTemp0Min = CalibrationParameterRanges.OXYGEN_TEMPERATURE_0.min;
  oxygenTemp0Max = CalibrationParameterRanges.OXYGEN_TEMPERATURE_0.max;
  oxygenTemp0Step = CalibrationParameterRanges.OXYGEN_TEMPERATURE_0.step;
  oxygenTemp100Min = CalibrationParameterRanges.OXYGEN_TEMPERATURE_100.min;
  oxygenTemp100Max = CalibrationParameterRanges.OXYGEN_TEMPERATURE_100.max;
  oxygenTemp100Step = CalibrationParameterRanges.OXYGEN_TEMPERATURE_100.step;
  oxygenPressureMin = CalibrationParameterRanges.OXYGEN_PRESSURE.min;
  oxygenPressureMax = CalibrationParameterRanges.OXYGEN_PRESSURE.max;
  oxygenPressureStep = CalibrationParameterRanges.OXYGEN_PRESSURE.step;

  phSlopeMin = CalibrationParameterRanges.PH_SLOPE.min;
  phSlopeMax = CalibrationParameterRanges.PH_SLOPE.max;
  phSlopeStep = CalibrationParameterRanges.PH_SLOPE.step;
  phSlopeBelowRangeWarning =
    CalibrationParameterRanges.PH_SLOPE.belowRangeWarning;
  phSlopeAboveRangeWarning =
    CalibrationParameterRanges.PH_SLOPE.aboveRangeWarning;
  phSlopeWarningMessage = CalibrationParameterRanges.PH_SLOPE.warningMessage;
  phOffsetMin = CalibrationParameterRanges.PH_OFFSET.min;
  phOffsetMax = CalibrationParameterRanges.PH_OFFSET.max;
  phOffsetStep = CalibrationParameterRanges.PH_OFFSET.step;
  phBelowRangeWarning = CalibrationParameterRanges.PH_OFFSET.belowRangeWarning;
  phBelowRangeWarningMessage =
    CalibrationParameterRanges.PH_OFFSET.warningMessage;
  phTemperatureMin = CalibrationParameterRanges.PH_TEMPERATURE.min;
  phTemperatureMax = CalibrationParameterRanges.PH_TEMPERATURE.max;
  phTemperatureStep = CalibrationParameterRanges.PH_TEMPERATURE.step;
  spargingSlopeMin = CalibrationParameterRanges.SPARGING_SLOPE.min;
  spargingSlopeMax = CalibrationParameterRanges.SPARGING_SLOPE.max;
  spargingSlopeStep = CalibrationParameterRanges.SPARGING_SLOPE.step;
  spargingInterceptMin = CalibrationParameterRanges.SPARGING_INTERCEPT.min;
  spargingInterceptMax = CalibrationParameterRanges.SPARGING_INTERCEPT.max;
  spargingInterceptStep = CalibrationParameterRanges.SPARGING_INTERCEPT.step;

  notesMinLength = NotesParameterRanges.CALIBRATION_NOTES.minNoteLength;
  notesMaxLength = NotesParameterRanges.CALIBRATION_NOTES.maxNoteLength;

  ModuleEnum = Module;
  Buffer = Buffer;
  selectedBuffers: BufferOption[] = [];

  // Create new OD calibration
  newODCalibration: Calibration;
  newManualODCalibration: ManualCalibration;

  // Create new Oxygen calibration
  newOxygenCalibration: OxygenCalibration;

  // Create new pH calibration
  newPHCalibration: PhCalibration;

  // Create new sparging calibration
  newSpargingCalibration: SpargingCalibration;

  /**
   * @param {Module} calibrationModuleType Type of calibration module
   */
  calibrationModuleType = Module.BaseModule;

  /**
   * @param {Device[]} deviceList A list of devices that can be selected for new calibration
   */
  deviceList: Device[] = [];

  /**
   * @param {{key: string, value: string}[]} slotList A list of save locations that can be selected for new calibration
   */
  slotList: { key: string; value: string }[] = [];

  // Shared params for all calibration types
  setupField = new FormBase<string>({
    key: 'setup',
    label: 'Setup',
    type: 'radio',
    placeholder: '',
    disabled: false,
    required: false,
    value: 'manual',
    options: [
      { key: 'manual', value: 'Manual' },
      { key: 'device', value: 'Device' },
    ],
  });

  calibrationTypeField = new FormBase<Module>({
    key: 'calibrationType',
    label: 'Select calibration type',
    type: 'radio',
    placeholder: '',
    disabled: false,
    required: false,
    value: Module.BaseModule,
    options: [
      { key: Module.BaseModule, value: 'Optical Density' },
      { key: Module.Oxygen, value: 'Oxygen' },
      { key: Module.pH, value: 'pH' },
      { key: Module.Sparging, value: 'Sparging' },
    ],
  });

  noteField = new FormBase({
    key: 'noteField',
    label: 'Note',
    type: 'textarea',
    placeholder: 'Enter notes here...',
    disabled: false,
    required: false,
    value: '',
    options: [],
  });

  deviceField = new FormBase({
    key: 'device',
    label: 'Select Device',
    type: 'dropdown',
    placeholder: 'Select...',
    disabled: false,
    required: true,
    value: null,
    options: [],
  });

  calibrationNameField = new FormBase({
    key: 'calibrationName',
    label: 'Calibration Name',
    type: 'text',
    placeholder: 'Calibration Name',
    disabled: false,
    required: true,
    value: null,
    options: [],
  });

  saveLocationField = new FormBase({
    key: 'saveLocation',
    label: 'Select Save Location',
    type: 'dropdown',
    placeholder: 'Select...',
    disabled: false,
    required: true,
    value: null,
    options: [],
  });

  // Optical Density calibration params
  temperatureControlAField = new FormBase<boolean>({
    key: 'temperatureControlA',
    label: 'Temperature Control A',
    type: 'toggle',
    placeholder: '',
    disabled: false,
    required: false,
    value: false,
    options: [],
  });

  temperatureControlBField = new FormBase<boolean>({
    key: 'temperatureControlB',
    label: 'Temperature Control B',
    type: 'toggle',
    placeholder: '',
    disabled: false,
    required: false,
    value: false,
    options: [],
  });

  temperatureControlCField = new FormBase<boolean>({
    key: 'temperatureControlC',
    label: 'Temperature Control C',
    type: 'toggle',
    placeholder: '',
    disabled: false,
    required: false,
    value: false,
    options: [],
  });

  temperatureControlDField = new FormBase<boolean>({
    key: 'temperatureControlD',
    label: 'Temperature Control D',
    type: 'toggle',
    placeholder: '',
    disabled: false,
    required: false,
    value: false,
    options: [],
  });

  inputTemperatureAField = new FormBase<number>({
    key: 'inputTemperatureA',
    label: 'Temperature A (°C)',
    type: 'number',
    placeholder: '',
    disabled: true,
    required: false,
    value: null,
    options: [],
  });

  inputTemperatureBField = new FormBase<number>({
    key: 'inputTemperatureB',
    label: 'Temperature B (°C)',
    type: 'number',
    placeholder: '',
    disabled: true,
    required: false,
    value: null,
    options: [],
  });

  inputTemperatureCField = new FormBase<number>({
    key: 'inputTemperatureC',
    label: 'Temperature C (°C)',
    type: 'number',
    placeholder: '',
    disabled: true,
    required: false,
    value: null,
    options: [],
  });

  inputTemperatureDField = new FormBase<number>({
    key: 'inputTemperatureD',
    label: 'Temperature D (°C)',
    type: 'number',
    placeholder: '',
    disabled: true,
    required: false,
    value: null,
    options: [],
  });

  // pH calibration params
  temperatureField = new FormBase<number>({
    key: 'temperature',
    label: 'Temperature (°C)',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: true,
    value: 25,
    options: [],
  });

  pHBuffer1Field = new FormBase({
    key: 'pHBuffer1',
    label: 'pH Buffer 1',
    type: 'dropdown',
    placeholder: 'Select...',
    disabled: false,
    required: true,
    value: null,
    options: pHBufferOptions,
  });

  pHBuffer2Field = new FormBase({
    key: 'pHBuffer2',
    label: 'pH Buffer 2',
    type: 'dropdown',
    placeholder: 'Select...',
    disabled: false,
    required: true,
    value: null,
    options: pHBufferOptions,
  });

  pHBuffer3Field = new FormBase({
    key: 'pHBuffer3',
    label: 'pH Buffer 3',
    type: 'dropdown',
    placeholder: 'Select...',
    disabled: false,
    required: false,
    value: null,
    options: pHBufferOptions,
  });

  pHBuffer4Field = new FormBase({
    key: 'pHBuffer4',
    label: 'pH Buffer 4',
    type: 'dropdown',
    placeholder: 'Select...',
    disabled: false,
    required: false,
    value: null,
    options: pHBufferOptions,
  });

  pHBuffer5Field = new FormBase({
    key: 'pHBuffer5',
    label: 'pH Buffer 5',
    type: 'dropdown',
    placeholder: 'Select...',
    disabled: false,
    required: false,
    value: null,
    options: pHBufferOptions,
  });

  // Sparging calibration params
  volumeField = new FormBase({
    key: 'volume',
    label: 'Volume (mL)',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: true,
    value: 50,
    options: [],
  });

  // Manual offset fields - OD
  offsetAField = new FormBase<number>({
    key: 'offsetA',
    label: 'Offset (p0)',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.OD_OFFSET.default,
    options: [],
  });

  offsetBField = new FormBase<number>({
    key: 'offsetB',
    label: 'Offset (p0)',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.OD_OFFSET.default,
    options: [],
  });

  offsetCField = new FormBase<number>({
    key: 'offsetC',
    label: 'Offset (p0)',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.OD_OFFSET.default,
    options: [],
  });

  offsetDField = new FormBase<number>({
    key: 'offsetD',
    label: 'Offset (p0)',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.OD_OFFSET.default,
    options: [],
  });

  // Manual factor fields - OD
  factorAField = new FormBase<number>({
    key: 'factorA',
    label: 'Factor (p1)',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.OD_FACTOR.default,
    options: [],
  });

  factorBField = new FormBase<number>({
    key: 'factorB',
    label: 'Factor (p1)',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.OD_FACTOR.default,
    options: [],
  });

  factorCField = new FormBase<number>({
    key: 'factorC',
    label: 'Factor (p1)',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.OD_FACTOR.default,
    options: [],
  });

  factorDField = new FormBase<number>({
    key: 'factorD',
    label: 'Factor (p1)',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.OD_FACTOR.default,
    options: [],
  });

  // Manual power fields - OD
  powerAField = new FormBase<number>({
    key: 'powerA',
    label: 'Power (p2)',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.OD_POWER.default,
    options: [],
  });

  powerBField = new FormBase<number>({
    key: 'powerB',
    label: 'Power (p2)',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.OD_POWER.default,
    options: [],
  });

  powerCField = new FormBase<number>({
    key: 'powerC',
    label: 'Power (p2)',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.OD_POWER.default,
    options: [],
  });

  powerDField = new FormBase<number>({
    key: 'powerD',
    label: 'Power (p2)',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.OD_POWER.default,
    options: [],
  });

  // Manual temperature fields - OD
  temperatureAField = new FormBase<number>({
    key: 'temperatureA',
    label: 'Temperature',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.OD_TEMPERATURE.default,
    options: [],
  });

  temperatureBField = new FormBase<number>({
    key: 'temperatureB',
    label: 'Temperature',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.OD_TEMPERATURE.default,
    options: [],
  });

  temperatureCField = new FormBase<number>({
    key: 'temperatureC',
    label: 'Temperature',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.OD_TEMPERATURE.default,
    options: [],
  });

  temperatureDField = new FormBase<number>({
    key: 'temperatureD',
    label: 'Temperature',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.OD_TEMPERATURE.default,
    options: [],
  });

  // Oxygen fields
  isZeroPercentField = new FormBase<boolean>({
    key: 'isZeroPercent',
    label: 'Oxygen saturation 0%',
    type: 'toggle',
    placeholder: '',
    disabled: false,
    required: false,
    value: false,
    options: [],
  });

  is100PercentField = new FormBase<boolean>({
    key: 'is100Percent',
    label: 'Oxygen saturation 100%',
    type: 'toggle',
    placeholder: '',
    disabled: false,
    required: false,
    value: true,
    options: [],
  });

  // Manual temperature fields - Oxygen 0%
  temperature0AField = new FormBase<number>({
    key: 'temperature0A',
    label: 'Temperature',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.OXYGEN_TEMPERATURE_0.default,
    options: [],
  });

  temperature0BField = new FormBase<number>({
    key: 'temperature0B',
    label: 'Temperature',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.OXYGEN_TEMPERATURE_0.default,
    options: [],
  });

  temperature0CField = new FormBase<number>({
    key: 'temperature0C',
    label: 'Temperature',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.OXYGEN_TEMPERATURE_0.default,
    options: [],
  });

  temperature0DField = new FormBase<number>({
    key: 'temperature0D',
    label: 'Temperature',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.OXYGEN_TEMPERATURE_0.default,
    options: [],
  });

  // Manual temperature fields - Oxygen 100%
  temperature100AField = new FormBase<number>({
    key: 'temperature100A',
    label: 'Temperature',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.OXYGEN_TEMPERATURE_100.default,
    options: [],
  });

  temperature100BField = new FormBase<number>({
    key: 'temperature100B',
    label: 'Temperature',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.OXYGEN_TEMPERATURE_100.default,
    options: [],
  });

  temperature100CField = new FormBase<number>({
    key: 'temperature100C',
    label: 'Temperature',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.OXYGEN_TEMPERATURE_100.default,
    options: [],
  });

  temperature100DField = new FormBase<number>({
    key: 'temperature100D',
    label: 'Temperature',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.OXYGEN_TEMPERATURE_100.default,
    options: [],
  });

  // dphi fields
  dphi100AField = new FormBase<number>({
    key: 'dphi100A',
    label: 'dphi',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.OXYGEN_DPHI_100.default,
    options: [],
  });

  dphi100BField = new FormBase<number>({
    key: 'dphi100B',
    label: 'dphi',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.OXYGEN_DPHI_100.default,
    options: [],
  });

  dphi100CField = new FormBase<number>({
    key: 'dphi100C',
    label: 'dphi',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.OXYGEN_DPHI_100.default,
    options: [],
  });

  dphi100DField = new FormBase<number>({
    key: 'dphi100D',
    label: 'dphi',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.OXYGEN_DPHI_100.default,
    options: [],
  });

  dphi0AField = new FormBase<number>({
    key: 'dphi0A',
    label: 'dphi',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.OXYGEN_DPHI_0.default,
    options: [],
  });

  dphi0BField = new FormBase<number>({
    key: 'dphi0B',
    label: 'dphi',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.OXYGEN_DPHI_0.default,
    options: [],
  });

  dphi0CField = new FormBase<number>({
    key: 'dphi0C',
    label: 'dphi',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.OXYGEN_DPHI_0.default,
    options: [],
  });

  dphi0DField = new FormBase<number>({
    key: 'dphi0D',
    label: 'dphi',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.OXYGEN_DPHI_0.default,
    options: [],
  });

  // Pressure fields
  pressure100AField = new FormBase<number>({
    key: 'pressure100A',
    label: 'Pressure',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.OXYGEN_PRESSURE.default,
    options: [],
  });

  pressure100BField = new FormBase<number>({
    key: 'pressure100B',
    label: 'Pressure',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.OXYGEN_PRESSURE.default,
    options: [],
  });

  pressure100CField = new FormBase<number>({
    key: 'pressure100C',
    label: 'Pressure',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.OXYGEN_PRESSURE.default,
    options: [],
  });

  pressure100DField = new FormBase<number>({
    key: 'pressure100D',
    label: 'Pressure',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.OXYGEN_PRESSURE.default,
    options: [],
  });

  // Manual pH fields
  phSlopeAField = new FormBase<number>({
    key: 'slopeA',
    label: 'Slope [%]',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.PH_SLOPE.default,
    options: [],
  });

  phSlopeBField = new FormBase<number>({
    key: 'slopeB',
    label: 'Slope [%]',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.PH_SLOPE.default,
    options: [],
  });

  phSlopeCField = new FormBase<number>({
    key: 'slopeC',
    label: 'Slope [%]',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.PH_SLOPE.default,
    options: [],
  });

  phSlopeDField = new FormBase<number>({
    key: 'slopeD',
    label: 'Slope [%]',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.PH_SLOPE.default,
    options: [],
  });

  phOffsetAField = new FormBase<number>({
    key: 'offsetA',
    label: 'Offset [mV]',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.PH_OFFSET.default,
    options: [],
  });

  phOffsetBField = new FormBase<number>({
    key: 'offsetB',
    label: 'Offset [mV]',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.PH_OFFSET.default,
    options: [],
  });

  phOffsetCField = new FormBase<number>({
    key: 'offsetC',
    label: 'Offset [mV]',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.PH_OFFSET.default,
    options: [],
  });

  phOffsetDField = new FormBase<number>({
    key: 'offsetD',
    label: 'Offset [mV]',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.PH_OFFSET.default,
    options: [],
  });

  // Manual sparging fields
  spargingSlopeAField = new FormBase<number>({
    key: 'slopeA',
    label: 'Slope',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.SPARGING_SLOPE.default,
    options: [],
  });

  spargingSlopeBField = new FormBase<number>({
    key: 'slopeB',
    label: 'Slope',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.SPARGING_SLOPE.default,
    options: [],
  });

  spargingSlopeCField = new FormBase<number>({
    key: 'slopeC',
    label: 'Slope',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.SPARGING_SLOPE.default,
    options: [],
  });

  spargingSlopeDField = new FormBase<number>({
    key: 'slopeD',
    label: 'Slope',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.SPARGING_SLOPE.default,
    options: [],
  });

  interceptAField = new FormBase<number>({
    key: 'interceptA',
    label: 'Intercept',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.SPARGING_INTERCEPT.default,
    options: [],
  });

  interceptBField = new FormBase<number>({
    key: 'interceptB',
    label: 'Intercept',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.SPARGING_INTERCEPT.default,
    options: [],
  });

  interceptCField = new FormBase<number>({
    key: 'interceptC',
    label: 'Intercept',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.SPARGING_INTERCEPT.default,
    options: [],
  });

  interceptDField = new FormBase<number>({
    key: 'interceptD',
    label: 'Intercept',
    type: 'number',
    placeholder: '',
    disabled: false,
    required: false,
    value: CalibrationParameterRanges.SPARGING_INTERCEPT.default,
    options: [],
  });

  calibrationForm = this.formBuilder.group({
    [this.setupField.key]: ['manual', []],
    [this.calibrationTypeField.key]: [Module.BaseModule, []],
    [this.noteField.key]: [
      '',
      [
        Validators.minLength(this.notesMinLength),
        Validators.maxLength(this.notesMaxLength),
      ],
    ],
    [this.deviceField.key]: [
      this.deviceField.value,
      [Validators.minLength(1), Validators.maxLength(100), Validators.required],
    ],
    [this.calibrationNameField.key]: [
      this.calibrationNameField.value,
      [
        Validators.minLength(2),
        Validators.maxLength(16),
        Validators.required,
        Validators.pattern(advancedNameRegex),
      ],
    ],
    [this.saveLocationField.key]: [
      {
        value: null,
        disabled: true,
      },
      [Validators.minLength(1), Validators.maxLength(100), Validators.required],
    ],
  });

  temperatureValuesForm = this.formBuilder.group({
    [this.temperatureControlAField.key]: [false, []],
    [this.temperatureControlBField.key]: [false, []],
    [this.temperatureControlCField.key]: [false, []],
    [this.temperatureControlDField.key]: [false, []],
    [this.inputTemperatureAField.key]: [
      {
        value: this.inputTemperatureAField.value,
        disabled: this.inputTemperatureAField.disabled,
      },
      [
        Validators.min(this.minTemperatureODAndOxygen),
        Validators.max(this.maxTemperatureODAndOxygen),
        Validators.required,
      ],
    ],
    [this.inputTemperatureBField.key]: [
      {
        value: this.inputTemperatureBField.value,
        disabled: this.inputTemperatureBField.disabled,
      },
      [
        Validators.min(this.minTemperatureODAndOxygen),
        Validators.max(this.maxTemperatureODAndOxygen),
        Validators.required,
      ],
    ],
    [this.inputTemperatureCField.key]: [
      {
        value: this.inputTemperatureCField.value,
        disabled: this.inputTemperatureCField.disabled,
      },
      [
        Validators.min(this.minTemperatureODAndOxygen),
        Validators.max(this.maxTemperatureODAndOxygen),
        Validators.required,
      ],
    ],
    [this.inputTemperatureDField.key]: [
      {
        value: this.inputTemperatureDField.value,
        disabled: this.inputTemperatureDField.disabled,
      },
      [
        Validators.min(this.minTemperatureODAndOxygen),
        Validators.max(this.maxTemperatureODAndOxygen),
        Validators.required,
      ],
    ],
  });

  pHForm = this.formBuilder.group({
    [this.temperatureField.key]: [
      this.temperatureField.value,
      [
        Validators.min(this.minTemperaturePH),
        Validators.max(this.maxTemperaturePH),
        Validators.required,
      ],
    ],
    [this.pHBuffer1Field.key]: [
      null,
      [
        Validators.required,
        BufferValidator(this.selectedBuffers, this.pHBuffer1Field.key),
      ],
    ],
    [this.pHBuffer2Field.key]: [
      null,
      [
        Validators.required,
        BufferValidator(this.selectedBuffers, this.pHBuffer2Field.key),
      ],
    ],
    [this.pHBuffer3Field.key]: [
      null,
      [BufferValidator(this.selectedBuffers, this.pHBuffer3Field.key)],
    ],
    [this.pHBuffer4Field.key]: [
      null,
      [BufferValidator(this.selectedBuffers, this.pHBuffer4Field.key)],
    ],
    [this.pHBuffer5Field.key]: [
      null,
      [BufferValidator(this.selectedBuffers, this.pHBuffer5Field.key)],
    ],
  });

  spargingForm = this.formBuilder.group({
    [this.volumeField.key]: [
      this.volumeField.value,
      [
        Validators.min(this.minVolume),
        Validators.max(this.maxVolume),
        Validators.required,
      ],
    ],
  });

  // Manual OD calibration form
  manualODCalibrationForm = this.formBuilder.group({
    [this.offsetAField.key]: [
      this.offsetAField.value,
      [
        Validators.required,
        Validators.min(this.odOffsetMin),
        Validators.max(this.odOffsetMax),
      ],
    ],
    [this.offsetBField.key]: [
      this.offsetBField.value,
      [
        Validators.required,
        Validators.min(this.odOffsetMin),
        Validators.max(this.odOffsetMax),
      ],
    ],
    [this.offsetCField.key]: [
      this.offsetCField.value,
      [
        Validators.required,
        Validators.min(this.odOffsetMin),
        Validators.max(this.odOffsetMax),
      ],
    ],
    [this.offsetDField.key]: [
      this.offsetDField.value,
      [
        Validators.required,
        Validators.min(this.odOffsetMin),
        Validators.max(this.odOffsetMax),
      ],
    ],
    [this.factorAField.key]: [
      this.factorAField.value,
      [
        Validators.required,
        Validators.min(this.odFactorMin),
        Validators.max(this.odFactorMax),
      ],
    ],
    [this.factorBField.key]: [
      this.factorBField.value,
      [
        Validators.required,
        Validators.min(this.odFactorMin),
        Validators.max(this.odFactorMax),
      ],
    ],
    [this.factorCField.key]: [
      this.factorCField.value,
      [
        Validators.required,
        Validators.min(this.odFactorMin),
        Validators.max(this.odFactorMax),
      ],
    ],
    [this.factorDField.key]: [
      this.factorDField.value,
      [
        Validators.required,
        Validators.min(this.odFactorMin),
        Validators.max(this.odFactorMax),
      ],
    ],
    [this.powerAField.key]: [
      this.powerAField.value,
      [
        Validators.required,
        Validators.min(this.odPowerMin),
        Validators.max(this.odPowerMax),
      ],
    ],
    [this.powerBField.key]: [
      this.powerBField.value,
      [
        Validators.required,
        Validators.min(this.odPowerMin),
        Validators.max(this.odPowerMax),
      ],
    ],
    [this.powerCField.key]: [
      this.powerCField.value,
      [
        Validators.required,
        Validators.min(this.odPowerMin),
        Validators.max(this.odPowerMax),
      ],
    ],
    [this.powerDField.key]: [
      this.powerDField.value,
      [
        Validators.required,
        Validators.min(this.odPowerMin),
        Validators.max(this.odPowerMax),
      ],
    ],
    [this.temperatureAField.key]: [
      this.temperatureAField.value,
      [
        Validators.required,
        Validators.min(this.odTemperatureMin),
        Validators.max(this.odTemperatureMax),
      ],
    ],
    [this.temperatureBField.key]: [
      this.temperatureBField.value,
      [
        Validators.required,
        Validators.min(this.odTemperatureMin),
        Validators.max(this.odTemperatureMax),
      ],
    ],
    [this.temperatureCField.key]: [
      this.temperatureCField.value,
      [
        Validators.required,
        Validators.min(this.odTemperatureMin),
        Validators.max(this.odTemperatureMax),
      ],
    ],
    [this.temperatureDField.key]: [
      this.temperatureDField.value,
      [
        Validators.required,
        Validators.min(this.odTemperatureMin),
        Validators.max(this.odTemperatureMax),
      ],
    ],
  });

  // Manual Oxygen calibration form
  manualOxygenCalibrationForm = this.formBuilder.group({
    [this.isZeroPercentField.key]: [
      this.isZeroPercentField.value,
      Validators.required,
    ],
    [this.is100PercentField.key]: [
      this.is100PercentField.value,
      Validators.required,
    ],
    [this.temperature100AField.key]: [
      this.temperature100AField.value,
      [
        Validators.required,
        Validators.min(this.oxygenTemp100Min),
        Validators.max(this.oxygenTemp100Max),
      ],
    ],
    [this.temperature100BField.key]: [
      this.temperature100BField.value,
      [
        Validators.required,
        Validators.min(this.oxygenTemp100Min),
        Validators.max(this.oxygenTemp100Max),
      ],
    ],
    [this.temperature100CField.key]: [
      this.temperature100CField.value,
      [
        Validators.required,
        Validators.min(this.oxygenTemp100Min),
        Validators.max(this.oxygenTemp100Max),
      ],
    ],
    [this.temperature100DField.key]: [
      this.temperature100DField.value,
      [
        Validators.required,
        Validators.min(this.oxygenTemp100Min),
        Validators.max(this.oxygenTemp100Max),
      ],
    ],
    [this.temperature0AField.key]: [
      this.temperature0AField.value,
      [
        Validators.min(this.oxygenTemp0Min),
        Validators.max(this.oxygenTemp0Max),
      ],
    ],
    [this.temperature0BField.key]: [
      this.temperature0BField.value,
      [
        Validators.min(this.oxygenTemp0Min),
        Validators.max(this.oxygenTemp0Max),
      ],
    ],
    [this.temperature0CField.key]: [
      this.temperature0CField.value,
      [
        Validators.min(this.oxygenTemp0Min),
        Validators.max(this.oxygenTemp0Max),
      ],
    ],
    [this.temperature0DField.key]: [
      this.temperature0DField.value,
      [
        Validators.min(this.oxygenTemp0Min),
        Validators.max(this.oxygenTemp0Max),
      ],
    ],
    [this.dphi100AField.key]: [
      this.dphi100AField.value,
      [
        Validators.required,
        Validators.min(this.oxygenDphi100Min),
        Validators.max(this.oxygenDphi100Max),
      ],
    ],
    [this.dphi100BField.key]: [
      this.dphi100BField.value,
      [
        Validators.required,
        Validators.min(this.oxygenDphi100Min),
        Validators.max(this.oxygenDphi100Max),
      ],
    ],
    [this.dphi100CField.key]: [
      this.dphi100CField.value,
      [
        Validators.required,
        Validators.min(this.oxygenDphi100Min),
        Validators.max(this.oxygenDphi100Max),
      ],
    ],
    [this.dphi100DField.key]: [
      this.dphi100DField.value,
      [
        Validators.required,
        Validators.min(this.oxygenDphi100Min),
        Validators.max(this.oxygenDphi100Max),
      ],
    ],
    [this.dphi0AField.key]: [
      this.dphi0AField.value,
      [
        Validators.min(this.oxygenDphi0Min),
        Validators.max(this.oxygenDphi0Max),
      ],
    ],
    [this.dphi0BField.key]: [
      this.dphi0BField.value,
      [
        Validators.min(this.oxygenDphi0Min),
        Validators.max(this.oxygenDphi0Max),
      ],
    ],
    [this.dphi0CField.key]: [
      this.dphi0CField.value,
      [
        Validators.min(this.oxygenDphi0Min),
        Validators.max(this.oxygenDphi0Max),
      ],
    ],
    [this.dphi0DField.key]: [
      this.dphi0DField.value,
      [
        Validators.min(this.oxygenDphi0Min),
        Validators.max(this.oxygenDphi0Max),
      ],
    ],
    [this.pressure100AField.key]: [
      this.pressure100AField.value,
      [
        Validators.required,
        Validators.min(this.oxygenPressureMin),
        Validators.max(this.oxygenPressureMax),
      ],
    ],
    [this.pressure100BField.key]: [
      this.pressure100BField.value,
      [
        Validators.required,
        Validators.min(this.oxygenPressureMin),
        Validators.max(this.oxygenPressureMax),
      ],
    ],
    [this.pressure100CField.key]: [
      this.pressure100CField.value,
      [
        Validators.required,
        Validators.min(this.oxygenPressureMin),
        Validators.max(this.oxygenPressureMax),
      ],
    ],
    [this.pressure100DField.key]: [
      this.pressure100DField.value,
      [
        Validators.required,
        Validators.min(this.oxygenPressureMin),
        Validators.max(this.oxygenPressureMax),
      ],
    ],
  });

  manualPHCalibrationForm = this.formBuilder.group({
    [this.phOffsetAField.key]: [
      this.phOffsetAField.value,
      [
        Validators.required,
        Validators.min(this.phOffsetMin),
        Validators.max(this.phOffsetMax),
      ],
    ],
    [this.phOffsetBField.key]: [
      this.phOffsetBField.value,
      [
        Validators.required,
        Validators.min(this.phOffsetMin),
        Validators.max(this.phOffsetMax),
      ],
    ],
    [this.phOffsetCField.key]: [
      this.phOffsetCField.value,
      [
        Validators.required,
        Validators.min(this.phOffsetMin),
        Validators.max(this.phOffsetMax),
      ],
    ],
    [this.phOffsetDField.key]: [
      this.phOffsetDField.value,
      [
        Validators.required,
        Validators.min(this.phOffsetMin),
        Validators.max(this.phOffsetMax),
      ],
    ],
    [this.phSlopeAField.key]: [
      this.phSlopeAField.value,
      [
        Validators.required,
        Validators.min(this.phSlopeMin),
        Validators.max(this.phSlopeMax),
      ],
    ],
    [this.phSlopeBField.key]: [
      this.phSlopeBField.value,
      [
        Validators.required,
        Validators.min(this.phSlopeMin),
        Validators.max(this.phSlopeMax),
      ],
    ],
    [this.phSlopeCField.key]: [
      this.phSlopeCField.value,
      [
        Validators.required,
        Validators.min(this.phSlopeMin),
        Validators.max(this.phSlopeMax),
      ],
    ],
    [this.phSlopeDField.key]: [
      this.phSlopeDField.value,
      [
        Validators.required,
        Validators.min(this.phSlopeMin),
        Validators.max(this.phSlopeMax),
      ],
    ],
  });

  manualSpargingCalibrationForm = this.formBuilder.group({
    [this.spargingSlopeAField.key]: [
      this.spargingSlopeAField.value,
      [
        Validators.required,
        Validators.min(this.spargingSlopeMin),
        Validators.max(this.spargingSlopeMax),
      ],
    ],
    [this.spargingSlopeBField.key]: [
      this.spargingSlopeBField.value,
      [
        Validators.required,
        Validators.min(this.spargingSlopeMin),
        Validators.max(this.spargingSlopeMax),
      ],
    ],
    [this.spargingSlopeCField.key]: [
      this.spargingSlopeCField.value,
      [
        Validators.required,
        Validators.min(this.spargingSlopeMin),
        Validators.max(this.spargingSlopeMax),
      ],
    ],
    [this.spargingSlopeDField.key]: [
      this.spargingSlopeDField.value,
      [
        Validators.required,
        Validators.min(this.spargingSlopeMin),
        Validators.max(this.spargingSlopeMax),
      ],
    ],
    [this.interceptAField.key]: [
      this.interceptAField.value,
      [
        Validators.required,
        Validators.min(this.spargingInterceptMin),
        Validators.max(this.spargingInterceptMax),
      ],
    ],
    [this.interceptBField.key]: [
      this.interceptBField.value,
      [
        Validators.required,
        Validators.min(this.spargingInterceptMin),
        Validators.max(this.spargingInterceptMax),
      ],
    ],
    [this.interceptCField.key]: [
      this.interceptCField.value,
      [
        Validators.required,
        Validators.min(this.spargingInterceptMin),
        Validators.max(this.spargingInterceptMax),
      ],
    ],
    [this.interceptDField.key]: [
      this.interceptDField.value,
      [
        Validators.required,
        Validators.min(this.spargingInterceptMin),
        Validators.max(this.spargingInterceptMax),
      ],
    ],
  });

  ngOnInit(): void {
    this.authenticationService.currentUser$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((user: User) => {
        this.currentUser = user;
      });

    this.laboratory = this.authenticationService.selectedLaboratory;

    this.navOptions = [
      {
        value: '100',
        labelText: '100%',
      },
      {
        value: '0',
        labelText: '0%',
      },
    ];

    if (this.laboratory) {
      // API call to get optical density devices by default when page loads
      this.getDevicesFromApi();

      // Set up form subscriptions
      this.setUpFormSubscriptions();

      const retrieveisCalibrationSetupCache =
        this.calibrationService.retrieveisCalibrationSetupCache;
      if (retrieveisCalibrationSetupCache) {
        this.calibrationForm.patchValue({
          [this.setupField.key]: retrieveisCalibrationSetupCache,
        });
        if (retrieveisCalibrationSetupCache === 'manual') {
          this.isManualCalibration = true;
        }
      }

      const cacheCalibrationData =
        this.calibrationService.retrieveAndClearUnsavedCalibrationCache;
      if (cacheCalibrationData) {
        this.calibrationModuleType = cacheCalibrationData.calibrationType;
        this.updateFormValues(cacheCalibrationData);
      }

      this.disableOxygenZeroPercentFields();
    }
  }

  setCalibrationNameValidators(maxLength: number): void {
    this.calibrationForm.controls[this.calibrationNameField.key].setValidators([
      Validators.minLength(2),
      Validators.maxLength(maxLength),
      Validators.required,
      Validators.pattern(advancedNameRegex),
    ]);
  }

  setUpFormSubscriptions(): void {
    // Fetch list of save locations when user selects device
    this.setupSubscription = this.calibrationForm
      .get(this.setupField.key)
      .valueChanges.subscribe((value) => {
        if (value === 'manual') {
          this.isManualCalibration = true;
        } else {
          this.isManualCalibration = false;
        }
        this.calibrationForm.controls['calibrationType'].enable();
      });

    // API calls to get save locations when the calibration type changes
    this.calibrationTypeSubscription = this.calibrationForm
      .get(this.calibrationTypeField.key)
      .valueChanges.subscribe((value) => {
        if (
          value !== null &&
          this.calibrationForm.controls['setup'].value !== null
        ) {
          this.displayFormFields = true;
          // Reset all dropdowns when user changes calibration type
          this.f[this.deviceField.key].reset();
          this.f[this.saveLocationField.key].reset();
          // Also reset memory slot list to empty
          this.slotList = [];
          // Set calibration module type and name validations based on user selection
          switch (value) {
            case Module.BaseModule:
              this.calibrationModuleType = Module.BaseModule;
              this.setCalibrationNameValidators(16);
              break;
            case Module.Oxygen:
              this.calibrationModuleType = Module.Oxygen;
              this.setCalibrationNameValidators(10);
              break;
            case Module.pH:
              this.calibrationModuleType = Module.pH;
              this.setCalibrationNameValidators(16);
              break;
            case Module.Sparging:
              this.calibrationModuleType = Module.Sparging;
              this.setCalibrationNameValidators(16);
              break;
          }

          // Common operations for all calibration types
          this.calibrationForm.controls[
            this.calibrationNameField.key
          ].updateValueAndValidity();
          this.getDevicesFromApi();
        }
      });

    // Fetch list of save locations when user selects device
    this.deviceSelectionSubscription = this.calibrationForm
      .get(this.deviceField.key)
      .valueChanges.subscribe((value) => {
        if (value) {
          this.getSaveLocations(value as number);
        }
      });

    /// Subscribe to temperature control changes for OD and Oxygen calibrations
    this.temperatureControlSubscriptions.push(
      this.temperatureValuesForm
        .get(this.temperatureControlAField.key)
        .valueChanges.subscribe(() => {
          // If temperature control has been set to false, disable the input field
          if (!this.tempF.temperatureControlA.value) {
            this.tempF.inputTemperatureA.reset();
            this.tempF.inputTemperatureA.disable();
          } else {
            this.tempF.inputTemperatureA.enable();
            this.tempF.inputTemperatureA.setValue(this.defaultTempValue);
          }
        }),
    );

    this.temperatureControlSubscriptions.push(
      this.temperatureValuesForm
        .get(this.temperatureControlBField.key)
        .valueChanges.subscribe(() => {
          if (!this.tempF.temperatureControlB.value) {
            this.tempF.inputTemperatureB.reset();
            this.tempF.inputTemperatureB.disable();
          } else {
            this.tempF.inputTemperatureB.enable();
            this.tempF.inputTemperatureB.setValue(this.defaultTempValue);
          }
        }),
    );

    this.temperatureControlSubscriptions.push(
      this.temperatureValuesForm
        .get(this.temperatureControlCField.key)
        .valueChanges.subscribe(() => {
          if (!this.tempF.temperatureControlC.value) {
            this.tempF.inputTemperatureC.reset();
            this.tempF.inputTemperatureC.disable();
          } else {
            this.tempF.inputTemperatureC.enable();
            this.tempF.inputTemperatureC.setValue(this.defaultTempValue);
          }
        }),
    );

    this.temperatureControlSubscriptions.push(
      this.temperatureValuesForm
        .get(this.temperatureControlDField.key)
        .valueChanges.subscribe(() => {
          if (!this.tempF.temperatureControlD.value) {
            this.tempF.inputTemperatureD.reset();
            this.tempF.inputTemperatureD.disable();
          } else {
            this.tempF.inputTemperatureD.enable();
            this.tempF.inputTemperatureD.setValue(this.defaultTempValue);
          }
        }),
    );

    this.pHBufferSubscriptions.push(
      this.pHForm
        .get(this.pHBuffer1Field.key)
        .valueChanges.subscribe((value) => {
          const index = this.selectedBuffers.findIndex(
            (buffer) => buffer.key === this.pHBuffer1Field.key,
          );
          if (index !== -1) {
            this.selectedBuffers.splice(index, 1);
          }
          this.selectedBuffers.push({
            key: this.pHBuffer1Field.key,
            value: value as Buffer,
          });
          this.updateFormValidation();
        }),
    );

    this.pHBufferSubscriptions.push(
      this.pHForm
        .get(this.pHBuffer2Field.key)
        .valueChanges.subscribe((value) => {
          const index = this.selectedBuffers.findIndex(
            (buffer) => buffer.key === this.pHBuffer2Field.key,
          );
          if (index !== -1) {
            this.selectedBuffers.splice(index, 1);
          }
          this.selectedBuffers.push({
            key: this.pHBuffer2Field.key,
            value: value as Buffer,
          });
          this.updateFormValidation();
        }),
    );

    this.pHBufferSubscriptions.push(
      this.pHForm
        .get(this.pHBuffer3Field.key)
        .valueChanges.subscribe((value) => {
          const index = this.selectedBuffers.findIndex(
            (buffer) => buffer.key === this.pHBuffer3Field.key,
          );
          if (index !== -1) {
            this.selectedBuffers.splice(index, 1);
          }
          this.selectedBuffers.push({
            key: this.pHBuffer3Field.key,
            value: value as Buffer,
          });
          this.updateFormValidation();
        }),
    );

    this.pHBufferSubscriptions.push(
      this.pHForm
        .get(this.pHBuffer4Field.key)
        .valueChanges.subscribe((value) => {
          const index = this.selectedBuffers.findIndex(
            (buffer) => buffer.key === this.pHBuffer4Field.key,
          );
          if (index !== -1) {
            this.selectedBuffers.splice(index, 1);
          }
          this.selectedBuffers.push({
            key: this.pHBuffer4Field.key,
            value: value as Buffer,
          });
          this.updateFormValidation();
        }),
    );

    this.pHBufferSubscriptions.push(
      this.pHForm
        .get(this.pHBuffer5Field.key)
        .valueChanges.subscribe((value) => {
          const index = this.selectedBuffers.findIndex(
            (buffer) => buffer.key === this.pHBuffer5Field.key,
          );
          if (index !== -1) {
            this.selectedBuffers.splice(index, 1);
          }
          this.selectedBuffers.push({
            key: this.pHBuffer5Field.key,
            value: value as Buffer,
          });
          this.updateFormValidation();
        }),
    );

    this.manualOxygenCalibrationForm
      .get(this.is100PercentField.key)
      .valueChanges.pipe(takeUntil(this.unsubscribe$))
      .subscribe((value) => {
        if (value) {
          this.enableOxygen100PercentFields();
        } else {
          this.manualOxygenCalibrationForm
            .get(this.isZeroPercentField.key)
            .setValue(true);
          this.disableOxygen100PercentFields();
        }
      });

    this.manualOxygenCalibrationForm
      .get(this.isZeroPercentField.key)
      .valueChanges.pipe(takeUntil(this.unsubscribe$))
      .subscribe((value) => {
        if (value) {
          this.enableOxygenZeroPercentFields();
        } else {
          this.manualOxygenCalibrationForm
            .get(this.is100PercentField.key)
            .setValue(true);
          this.disableOxygenZeroPercentFields();
        }
      });
  }

  enableOxygen100PercentFields(): void {
    (['A', 'B', 'C', 'D'] as const).forEach((flask) => {
      this.manualOxygenF[
        this[`temperature100${flask}Field` as const].key
      ].enable();
      this.manualOxygenF[
        this[`temperature100${flask}Field` as const].key
      ].setValidators([
        Validators.required,
        Validators.min(this.oxygenTemp100Min),
        Validators.max(this.oxygenTemp100Max),
      ]);
      this.manualOxygenF[
        this[`temperature100${flask}Field` as const].key
      ].updateValueAndValidity();
      this.manualOxygenF[this[`dphi100${flask}Field` as const].key].enable();
      this.manualOxygenF[
        this[`dphi100${flask}Field` as const].key
      ].setValidators([
        Validators.required,
        Validators.min(this.oxygenTemp100Min),
        Validators.max(this.oxygenTemp100Max),
      ]);
      this.manualOxygenF[
        this[`dphi100${flask}Field` as const].key
      ].updateValueAndValidity();
      this.manualOxygenF[
        this[`pressure100${flask}Field` as const].key
      ].enable();
      this.manualOxygenF[
        this[`pressure100${flask}Field` as const].key
      ].setValidators([
        Validators.required,
        Validators.min(this.oxygenTemp100Min),
        Validators.max(this.oxygenTemp100Max),
      ]);
      this.manualOxygenF[
        this[`pressure100${flask}Field` as const].key
      ].updateValueAndValidity();
    });
  }

  disableOxygen100PercentFields(): void {
    (['A', 'B', 'C', 'D'] as const).forEach((flask) => {
      this.manualOxygenF[
        this[`temperature100${flask}Field` as const].key
      ].disable();
      this.manualOxygenF[this[`dphi100${flask}Field` as const].key].disable();
      this.manualOxygenF[
        this[`pressure100${flask}Field` as const].key
      ].disable();
    });
  }

  enableOxygenZeroPercentFields(): void {
    (['A', 'B', 'C', 'D'] as const).forEach((flask) => {
      this.manualOxygenF[
        this[`temperature0${flask}Field` as const].key
      ].enable();
      this.manualOxygenF[
        this[`temperature0${flask}Field` as const].key
      ].setValidators([
        Validators.required,
        Validators.min(this.oxygenTemp0Min),
        Validators.max(this.oxygenTemp0Max),
      ]);
      this.manualOxygenF[
        this[`temperature0${flask}Field` as const].key
      ].updateValueAndValidity();
      this.manualOxygenF[this[`dphi0${flask}Field` as const].key].enable();
      this.manualOxygenF[this[`dphi0${flask}Field` as const].key].setValidators(
        [
          Validators.required,
          Validators.min(this.oxygenTemp0Min),
          Validators.max(this.oxygenTemp0Max),
        ],
      );
      this.manualOxygenF[
        this[`dphi0${flask}Field` as const].key
      ].updateValueAndValidity();
    });
  }

  disableOxygenZeroPercentFields(): void {
    (['A', 'B', 'C', 'D'] as const).forEach((flask) => {
      this.manualOxygenF[
        this[`temperature0${flask}Field` as const].key
      ].disable();
      this.manualOxygenF[this[`dphi0${flask}Field` as const].key].disable();
    });
  }

  updateFormValidation(): void {
    this.pHf[this.pHBuffer1Field.key].updateValueAndValidity({
      onlySelf: true,
      emitEvent: false,
    });
    this.pHf[this.pHBuffer2Field.key].updateValueAndValidity({
      onlySelf: true,
      emitEvent: false,
    });
    this.pHf[this.pHBuffer3Field.key].updateValueAndValidity({
      onlySelf: true,
      emitEvent: false,
    });
    this.pHf[this.pHBuffer4Field.key].updateValueAndValidity({
      onlySelf: true,
      emitEvent: false,
    });
    this.pHf[this.pHBuffer5Field.key].updateValueAndValidity({
      onlySelf: true,
      emitEvent: false,
    });
  }

  updateFormValues(
    cacheCalibrationData:
      | Calibration
      | ManualCalibration
      | OxygenCalibration
      | PhCalibration
      | SpargingCalibration,
  ): void {
    // Patch the values common to all calibrations
    this.calibrationForm.patchValue({
      calibrationType: cacheCalibrationData.calibrationType,
      calibrationName: cacheCalibrationData.name,
    });

    switch (this.calibrationModuleType as Module) {
      case Module.BaseModule:
        const calibration = cacheCalibrationData as Calibration;
        this.temperatureValuesForm.patchValue({
          temperatureControlA: calibration.temperatureControlA,
          temperatureControlB: calibration.temperatureControlB,
          temperatureControlC: calibration.temperatureControlC,
          temperatureControlD: calibration.temperatureControlD,
          inputTemperatureA: calibration.temperatureControlA
            ? calibration.inputTemperatureA
            : null,
          inputTemperatureB: calibration.temperatureControlB
            ? calibration.inputTemperatureB
            : null,
          inputTemperatureC: calibration.temperatureControlC
            ? calibration.inputTemperatureC
            : null,
          inputTemperatureD: calibration.temperatureControlD
            ? calibration.inputTemperatureD
            : null,
        });
        const manualCalibration = cacheCalibrationData as ManualCalibration;
        this.manualODCalibrationForm.patchValue({
          temperatureA: manualCalibration.resultsTemperatureA,
          temperatureB: manualCalibration.resultsTemperatureB,
          temperatureC: manualCalibration.resultsTemperatureC,
          temperatureD: manualCalibration.resultsTemperatureD,
          factorA: manualCalibration.calFactor_A,
          factorB: manualCalibration.calFactor_B,
          factorC: manualCalibration.calFactor_C,
          factorD: manualCalibration.calFactor_D,
          offsetA: manualCalibration.calOffset_A,
          offsetB: manualCalibration.calOffset_B,
          offsetC: manualCalibration.calOffset_C,
          offsetD: manualCalibration.calOffset_D,
          powerA: manualCalibration.calPower_A,
          powerB: manualCalibration.calPower_B,
          powerC: manualCalibration.calPower_C,
          powerD: manualCalibration.calPower_D,
        });
        return;
      case Module.Oxygen:
        const oxygenCalibration = cacheCalibrationData as OxygenCalibration;
        this.temperatureValuesForm.patchValue({
          temperatureControlA: oxygenCalibration.temperatureControlA,
          temperatureControlB: oxygenCalibration.temperatureControlB,
          temperatureControlC: oxygenCalibration.temperatureControlC,
          temperatureControlD: oxygenCalibration.temperatureControlD,
          inputTemperatureA: oxygenCalibration.temperatureControlA
            ? oxygenCalibration.inputTemperatureA
            : null,
          inputTemperatureB: oxygenCalibration.temperatureControlB
            ? oxygenCalibration.inputTemperatureB
            : null,
          inputTemperatureC: oxygenCalibration.temperatureControlC
            ? oxygenCalibration.inputTemperatureC
            : null,
          inputTemperatureD: oxygenCalibration.temperatureControlD
            ? oxygenCalibration.inputTemperatureD
            : null,
        });
        const manualOxygenCalibration =
          cacheCalibrationData as OxygenCalibration;
        this.manualOxygenCalibrationForm.patchValue({
          isZeroPercent: manualOxygenCalibration.isZeroPercent,
          is100Percent: manualOxygenCalibration.is100Percent,
          temperature100A: manualOxygenCalibration.temperature_100_A,
          temperature100B: manualOxygenCalibration.temperature_100_B,
          temperature100C: manualOxygenCalibration.temperature_100_C,
          temperature100D: manualOxygenCalibration.temperature_100_D,
          temperature0A: manualOxygenCalibration.temperature_0_A,
          temperature0B: manualOxygenCalibration.temperature_0_B,
          temperature0C: manualOxygenCalibration.temperature_0_C,
          temperature0D: manualOxygenCalibration.temperature_0_D,
          dphi100A: manualOxygenCalibration.dphi_100_A,
          dphi100B: manualOxygenCalibration.dphi_100_B,
          dphi100C: manualOxygenCalibration.dphi_100_C,
          dphi100D: manualOxygenCalibration.dphi_100_D,
          dphi0A: manualOxygenCalibration.dphi_0_A,
          dphi0B: manualOxygenCalibration.dphi_0_B,
          dphi0C: manualOxygenCalibration.dphi_0_C,
          dphi0D: manualOxygenCalibration.dphi_0_D,
          pressure100A: manualOxygenCalibration.pressure_100_A,
          pressure100B: manualOxygenCalibration.pressure_100_B,
          pressure100C: manualOxygenCalibration.pressure_100_C,
          pressure100D: manualOxygenCalibration.pressure_100_D,
        });
        return;
      case Module.pH:
        const phCalibration = cacheCalibrationData as PhCalibration;
        this.pHForm.patchValue({
          temperature: phCalibration.temperature,
          pHBuffer1: phCalibration.phBufferEnums[0],
          pHBuffer2: phCalibration.phBufferEnums[1],
          pHBuffer3: phCalibration.phBufferEnums[2],
          pHBuffer4: phCalibration.phBufferEnums[3],
          pHBuffer5: phCalibration.phBufferEnums[4],
        });
        const manualPHCalibration = cacheCalibrationData as PhCalibration;
        this.manualPHCalibrationForm.patchValue({
          offsetA: manualPHCalibration.offset_A,
          offsetB: manualPHCalibration.offset_B,
          offsetC: manualPHCalibration.offset_C,
          offsetD: manualPHCalibration.offset_D,
          slopeA: manualPHCalibration.slope_A,
          slopeB: manualPHCalibration.slope_B,
          slopeC: manualPHCalibration.slope_C,
          slopeD: manualPHCalibration.slope_D,
        });
        return;
      case Module.Sparging:
        const spargingCalibration = cacheCalibrationData as SpargingCalibration;
        this.spargingForm.patchValue({
          volume: spargingCalibration.volume,
        });
        const manualSpargingCalibration =
          cacheCalibrationData as SpargingCalibration;
        this.manualSpargingCalibrationForm.patchValue({
          slopeA: manualSpargingCalibration.slope_A,
          slopeB: manualSpargingCalibration.slope_B,
          slopeC: manualSpargingCalibration.slope_C,
          slopeD: manualSpargingCalibration.slope_D,
          interceptA: manualSpargingCalibration.intercept_A,
          interceptB: manualSpargingCalibration.intercept_B,
          interceptC: manualSpargingCalibration.intercept_C,
          interceptD: manualSpargingCalibration.intercept_D,
        });
        return;
      default:
        return;
    }
  }

  /**
   * @remarks
   * Convenience getter for easy access to form fields
   *
   */
  get f(): FormGroup['controls'] {
    return this.calibrationForm.controls;
  }

  /**
   * @remarks
   * Convenience getter for easy access to form fields
   *
   */
  get manualODF(): FormGroup['controls'] {
    return this.manualODCalibrationForm.controls;
  }

  /**
   * @remarks
   * Convenience getter for easy access to form fields
   *
   */
  get manualOxygenF(): FormGroup['controls'] {
    return this.manualOxygenCalibrationForm.controls;
  }

  /**
   * @remarks
   * Convenience getter for easy access to form fields
   *
   */
  get manualPHF(): FormGroup['controls'] {
    return this.manualPHCalibrationForm.controls;
  }

  /**
   * @remarks
   * Convenience getter for easy access to form fields
   *
   */
  get manualSpargingF(): FormGroup['controls'] {
    return this.manualSpargingCalibrationForm.controls;
  }

  /**
   * @remarks
   * Convenience getter for easy access to pH value form fields
   */
  get pHf(): FormGroup['controls'] {
    return this.pHForm.controls;
  }

  /**
   * @remarks
   * Convenience getter for easy access to temperature values form fields
   */
  get tempF(): FormGroup['controls'] {
    return this.temperatureValuesForm.controls;
  }

  /**
   * @remarks
   * Convenience getter for easy access to temperature values form fields
   */
  get spargingF(): FormGroup['controls'] {
    return this.spargingForm.controls;
  }

  collectPHBufferIds(): Buffer[] {
    const keys = [
      this.pHBuffer1Field.key,
      this.pHBuffer2Field.key,
      this.pHBuffer3Field.key,
      this.pHBuffer4Field.key,
      this.pHBuffer5Field.key,
    ];
    const bufferIds: Buffer[] = [];
    keys.forEach((key) => {
      const val = this.pHForm.get(key).value;
      if (val || val === Buffer.Custom) {
        bufferIds.push(val as Buffer);
      }
    });
    return bufferIds;
  }

  private applyInputTemperaturesTo(cal: any): void {
    if (
      this.temperatureValuesForm.get(this.temperatureControlAField.key).value
    ) {
      cal.inputTemperatureA = this.temperatureValuesForm.get(
        this.inputTemperatureAField.key,
      ).value as number;
    }
    if (
      this.temperatureValuesForm.get(this.temperatureControlBField.key).value
    ) {
      cal.inputTemperatureB = this.temperatureValuesForm.get(
        this.inputTemperatureBField.key,
      ).value as number;
    }
    if (
      this.temperatureValuesForm.get(this.temperatureControlCField.key).value
    ) {
      cal.inputTemperatureC = this.temperatureValuesForm.get(
        this.inputTemperatureCField.key,
      ).value as number;
    }
    if (
      this.temperatureValuesForm.get(this.temperatureControlDField.key).value
    ) {
      cal.inputTemperatureD = this.temperatureValuesForm.get(
        this.inputTemperatureDField.key,
      ).value as number;
    }
  }

  resetForm(): void {
    this.calibrationForm.reset();
    this.calibrationService.onUpdateCreateCalibrationFormCache(null);
  }

  onSubmitCreateCalibrationForm(): void {
    this.error = null;
    const deviceId = this.f.device.value as number;

    switch (this.calibrationModuleType) {
      case Module.BaseModule:
        if (this.isManualCalibration) {
          this.createManualODCalibration(deviceId);
        } else {
          this.createODCalibration(deviceId);
        }
        break;
      case Module.Oxygen:
        if (this.isManualCalibration) {
          this.createManualOxygenCalibration(deviceId);
        } else {
          this.createOxygenCalibration(deviceId);
        }
        break;
      case Module.pH:
        if (this.isManualCalibration) {
          this.createManualPHCalibration(deviceId);
        } else {
          this.createPHCalibration(deviceId);
        }
        break;
      case Module.Sparging:
        if (this.isManualCalibration) {
          this.createManualSpargingCalibration(deviceId);
        } else {
          this.createSpargingCalibration(deviceId);
        }
        break;
    }
  }

  createManualODCalibration(deviceId: number): void {
    if (this.isProcessing) {
      return;
    }
    this.error = null;
    this.isProcessing = true;

    this.setCurrentManualODFormValues(deviceId);

    this.calibrationService
      .createManualODCalibration(this.newManualODCalibration)
      .subscribe({
        next: (id: number) => {
          this.isProcessing = false;
          this.calibrationForm.markAsPristine();
          void this.router.navigate([
            `calibration/optical-density/${id}/calibration-data`,
          ]);
        },
        error: (error: ErrorObject) => {
          this.isProcessing = false;
          this.error = error;
        },
      });
  }

  createODCalibration(deviceId: number): void {
    if (this.isProcessing) {
      return;
    }
    this.error = null;
    this.isProcessing = true;

    this.setCurrentODFormValues(deviceId);

    this.calibrationService
      .createODCalibration(this.newODCalibration)
      .subscribe({
        next: (id: number) => {
          this.isProcessing = false;
          this.calibrationForm.markAsPristine();
          void this.router.navigate([
            `calibration/optical-density/${id}/calibration-data`,
          ]);
        },
        error: (error: ErrorObject) => {
          this.isProcessing = false;
          this.error = error;
        },
      });
  }

  createManualOxygenCalibration(deviceId: number): void {
    if (this.isProcessing) {
      return;
    }

    this.error = null;
    this.isProcessing = true;

    this.setCurrentManualOxygenFormValues(deviceId);

    this.calibrationService
      .createManualOxygenCalibration(this.newOxygenCalibration)
      .subscribe({
        next: (id: number) => {
          this.isProcessing = false;
          this.calibrationForm.markAsPristine();
          void this.router.navigate([
            `calibration/oxygen/${id}/calibration-data`,
          ]);
        },
        error: (error: ErrorObject) => {
          this.isProcessing = false;
          this.error = error;
        },
      });
  }

  createOxygenCalibration(deviceId: number): void {
    if (this.isProcessing) {
      return;
    }

    this.error = null;
    this.isProcessing = true;

    this.setCurrentOxygenFormValues(deviceId);

    this.calibrationService
      .createOxygenCalibration(this.newOxygenCalibration)
      .subscribe({
        next: (id: number) => {
          this.isProcessing = false;
          this.calibrationForm.markAsPristine();
          void this.router.navigate([
            `calibration/oxygen/${id}/calibration-data`,
          ]);
        },
        error: (error: ErrorObject) => {
          this.isProcessing = false;
          this.error = error;
        },
      });
  }

  createManualPHCalibration(deviceId: number): void {
    if (this.isProcessing) {
      return;
    }

    this.error = null;
    this.isProcessing = true;

    this.setCurrentManualPHFormValues(deviceId);

    this.calibrationService
      .createManualPHCalibration(this.newPHCalibration)
      .subscribe({
        next: (id: number) => {
          this.isProcessing = false;
          this.calibrationForm.markAsPristine();
          void this.router.navigate([
            `calibration/ph/${id}/calibration-data`,
          ]);
        },
        error: (error: ErrorObject) => {
          this.isProcessing = false;
          this.error = error;
        },
      });
  }

  createPHCalibration(deviceId: number): void {
    if (this.isProcessing) {
      return;
    }

    this.error = null;
    this.isProcessing = true;

    this.setCurrentPHFormValues(deviceId);

    this.calibrationService
      .createPHCalibration(this.newPHCalibration)
      .subscribe({
        next: (id: number) => {
          this.isProcessing = false;
          this.calibrationForm.markAsPristine();
          void this.router.navigate([
            `calibration/ph/${id}/calibration-data`,
          ]);
        },
        error: (error: ErrorObject) => {
          this.isProcessing = false;
          this.error = error;
        },
      });
  }

  createManualSpargingCalibration(deviceId: number): void {
    if (this.isProcessing) {
      return;
    }
    this.error = null;
    this.isProcessing = true;

    this.setCurrentManualSpargingFormValues(deviceId);

    this.calibrationService
      .createManualSpargingCalibration(this.newSpargingCalibration)
      .subscribe({
        next: (id: number) => {
          this.isProcessing = false;
          this.calibrationForm.markAsPristine();
          void this.router.navigate([
            `calibration/sparging/${id}/calibration-data`,
          ]);
        },
        error: (error: ErrorObject) => {
          this.isProcessing = false;
          this.error = error;
        },
      });
  }

  createSpargingCalibration(deviceId: number): void {
    if (this.isProcessing) {
      return;
    }

    this.error = null;
    this.isProcessing = true;

    this.setCurrentSpargingFormValues(deviceId);

    this.calibrationService
      .createSpargingCalibration(this.newSpargingCalibration)
      .subscribe({
        next: (id: number) => {
          this.isProcessing = false;
          this.calibrationForm.markAsPristine();
          void this.router.navigate([
            `calibration/sparging/${id}/calibration-data`,
          ]);
        },
        error: (error: ErrorObject) => {
          this.isProcessing = false;
          this.error = error;
        },
      });
  }

  getDevicesFromApi(): void {
    // Cancel any existing device API calls
    if (this.deviceSubscription) {
      this.deviceSubscription.unsubscribe();
    }
    // Set loading to true and clear previous errors
    this.devicesLoading = true;
    this.error = null;

    // Clear previous device list and dropdown options before fetching devices again
    this.deviceList = [];
    this.deviceField.options = [];

    this.deviceSubscription = this.deviceService
      .getDevices(
        'Name',
        'desc',
        null,
        null,
        [this.calibrationModuleType],
        DeviceSetupStatus.Inactive, // Only return devices that are inactive
        false, // Do not return devices that are not online
      )
      .subscribe({
        next: (response: DeviceIndexResponse) => {
          this.deviceList = response.deviceList;
          // Populate device dropdown options
          response.deviceList.forEach((device: Device) => {
            this.deviceField.options.push({
              key: device.id,
              value: device.name,
            });
          });
          this.devicesLoading = false;
        },
        error: (error: ErrorObject) => {
          this.devicesLoading = false;
          this.error = error;
        },
      });
  }

  getSaveLocations(deviceId: number): void {
    this.saveLocationsLoading = true;
    // Disable dropdowns and clear previous values before fetching save slot locations again
    this.f.saveLocation.disable();
    this.slotList = [];
    this.saveLocationField.options = [];
    this.error = null;
    this.calibrationService
      .getSlotSaveLocations(deviceId, this.calibrationModuleType)
      .subscribe({
        next: (response: CalibrationSlot[]) => {
          if (response.length > 0) {
            response.forEach((slot: CalibrationSlot) => {
              this.slotList.push({
                key: slot.slotName,
                value: slot.displayName,
              });
            });
            this.saveLocationField.options = this.slotList;
            this.f.saveLocation.enable();
            this.saveLocationsLoading = false;
          } else {
            // If there are no calibrations set list to null not empty array
            // so the loading spinner/no calibrations message renders correctly
            this.slotList = null;
          }
          this.saveLocationsLoading = false;
        },
        error: (error: ErrorObject) => {
          this.saveLocationsLoading = false;
          this.error = error;
        },
      });
  }

  openModal(modalId: string): void {
    this.modalService.open(modalId);
  }

  closeModal(modalId: string): void {
    this.modalService.close(modalId);
  }

  modalButtonClicked(buttonId: string): void {
    switch (buttonId) {
      case 'cancel-button':
        this.allowNavigation.next(false);
        this.closeModal('attention');
        break;
      case 'confirm-navigation':
        this.allowNavigation.next(true);
        this.closeModal('attention');
        break;
    }
  }

  getCurrentFormValues():
    | Calibration
    | ManualCalibration
    | OxygenCalibration
    | PhCalibration
    | SpargingCalibration {
    const deviceId = this.f.device.value as number;

    // Set calibration type for form cache
    switch (this.calibrationModuleType) {
      case Module.BaseModule:
        if (this.isManualCalibration) {
          this.setCurrentManualODFormValues(deviceId);
          this.newManualODCalibration.calibrationType =
            this.calibrationModuleType;
          return this.newManualODCalibration;
        } else {
          this.setCurrentODFormValues(deviceId);
          this.newODCalibration.calibrationType = this.calibrationModuleType;
          return this.newODCalibration;
        }
      case Module.Oxygen:
        this.newOxygenCalibration.calibrationType = this.calibrationModuleType;
        if (this.isManualCalibration) {
          this.setCurrentManualOxygenFormValues(deviceId);
          return this.newOxygenCalibration;
        } else {
          this.setCurrentOxygenFormValues(deviceId);
          return this.newOxygenCalibration;
        }
      case Module.pH:
        this.newPHCalibration.calibrationType = this.calibrationModuleType;
        if (this.isManualCalibration) {
          this.setCurrentManualPHFormValues(deviceId);
          return this.newPHCalibration;
        } else {
          this.setCurrentPHFormValues(deviceId);
          return this.newPHCalibration;
        }
      case Module.Sparging:
        this.newSpargingCalibration.calibrationType =
          this.calibrationModuleType;
        if (this.isManualCalibration) {
          this.setCurrentManualSpargingFormValues(deviceId);
          return this.newSpargingCalibration;
        } else {
          this.setCurrentSpargingFormValues(deviceId);
          return this.newSpargingCalibration;
        }
      default:
        return null;
    }
  }

  setCurrentManualODFormValues(deviceId: number): void {
    this.newManualODCalibration = new ManualCalibration(
      deviceId,
      this.f.calibrationName.value as string,
      this.f.saveLocation.value as string,
      this.manualODF.temperatureA.value as number,
      this.manualODF.temperatureB.value as number,
      this.manualODF.temperatureC.value as number,
      this.manualODF.temperatureD.value as number,
      this.manualODF.factorA.value as number,
      this.manualODF.factorB.value as number,
      this.manualODF.factorC.value as number,
      this.manualODF.factorD.value as number,
      this.manualODF.offsetA.value as number,
      this.manualODF.offsetB.value as number,
      this.manualODF.offsetC.value as number,
      this.manualODF.offsetD.value as number,
      this.manualODF.powerA.value as number,
      this.manualODF.powerB.value as number,
      this.manualODF.powerC.value as number,
      this.manualODF.powerD.value as number,
    );

    this.newManualODCalibration.notes = this.f.noteField.value as string;
  }

  setCurrentODFormValues(deviceId: number): void {
    this.newODCalibration = new Calibration(
      deviceId,
      this.f.calibrationName.value as string,
      this.f.saveLocation.value as string,
      this.temperatureValuesForm.value.temperatureControlA as boolean,
      this.temperatureValuesForm.value.temperatureControlB as boolean,
      this.temperatureValuesForm.value.temperatureControlC as boolean,
      this.temperatureValuesForm.value.temperatureControlD as boolean,
    );

    this.newODCalibration.notes = this.f.noteField.value as string;

    // Set conditional temperature values
    this.applyInputTemperaturesTo(this.newODCalibration);
  }

  setCurrentManualOxygenFormValues(deviceId: number): void {
    this.newOxygenCalibration = new OxygenCalibration(
      deviceId,
      this.f.calibrationName.value as string,
      this.f.saveLocation.value as string,
      null, // temperatureControlA
      null, // temperatureControlB
      null, // temperatureControlC
      null, // temperatureControlD
      null, // inputTemperatureA
      null, // inputTemperatureB
      null, // inputTemperatureC
      null, // inputTemperatureD
      this.manualOxygenF.isZeroPercent.value,
      this.manualOxygenF.is100Percent.value,
      this.manualOxygenF.isZeroPercent.value
        ? this.manualOxygenF.temperature0A.value
        : null,
      this.manualOxygenF.isZeroPercent.value
        ? this.manualOxygenF.temperature0B.value
        : null,
      this.manualOxygenF.isZeroPercent.value
        ? this.manualOxygenF.temperature0C.value
        : null,
      this.manualOxygenF.isZeroPercent.value
        ? this.manualOxygenF.temperature0D.value
        : null,
      this.manualOxygenF.is100Percent.value
        ? this.manualOxygenF.temperature100A.value
        : null,
      this.manualOxygenF.is100Percent.value
        ? this.manualOxygenF.temperature100B.value
        : null,
      this.manualOxygenF.is100Percent.value
        ? this.manualOxygenF.temperature100C.value
        : null,
      this.manualOxygenF.is100Percent.value
        ? this.manualOxygenF.temperature100D.value
        : null,
      this.manualOxygenF.is100Percent.value
        ? this.manualOxygenF.pressure100A.value
        : null,
      this.manualOxygenF.is100Percent.value
        ? this.manualOxygenF.pressure100B.value
        : null,
      this.manualOxygenF.is100Percent.value
        ? this.manualOxygenF.pressure100C.value
        : null,
      this.manualOxygenF.is100Percent.value
        ? this.manualOxygenF.pressure100D.value
        : null,
      this.manualOxygenF.isZeroPercent.value
        ? this.manualOxygenF.dphi0A.value
        : null,
      this.manualOxygenF.isZeroPercent.value
        ? this.manualOxygenF.dphi0B.value
        : null,
      this.manualOxygenF.isZeroPercent.value
        ? this.manualOxygenF.dphi0C.value
        : null,
      this.manualOxygenF.isZeroPercent.value
        ? this.manualOxygenF.dphi0D.value
        : null,
      this.manualOxygenF.is100Percent.value
        ? this.manualOxygenF.dphi100A.value
        : null,
      this.manualOxygenF.is100Percent.value
        ? this.manualOxygenF.dphi100B.value
        : null,
      this.manualOxygenF.is100Percent.value
        ? this.manualOxygenF.dphi100C.value
        : null,
      this.manualOxygenF.is100Percent.value
        ? this.manualOxygenF.dphi100D.value
        : null,
    );

    this.newOxygenCalibration.notes = this.f.noteField.value as string;
  }

  setCurrentOxygenFormValues(deviceId: number): void {
    this.newOxygenCalibration = new OxygenCalibration(
      deviceId,
      this.f.calibrationName.value as string,
      this.f.saveLocation.value as string,
      this.tempF.temperatureControlA.value as boolean,
      this.tempF.temperatureControlB.value as boolean,
      this.tempF.temperatureControlC.value as boolean,
      this.tempF.temperatureControlD.value as boolean,
    );

    this.newOxygenCalibration.notes = this.f.noteField.value as string;

    // Set conditional temperature values
    this.applyInputTemperaturesTo(this.newOxygenCalibration);
  }

  setCurrentManualPHFormValues(deviceId: number): void {
    this.newPHCalibration = new PhCalibration(
      deviceId,
      this.f.calibrationName.value as string,
      this.f.saveLocation.value as string,
      false, // Control has been hidden and is always false per Ogi request
      this.pHf.temperature.value as number,
      null, // No buffers for manual calibration
      this.manualPHF.slopeA.value as number,
      this.manualPHF.slopeB.value as number,
      this.manualPHF.slopeC.value as number,
      this.manualPHF.slopeD.value as number,
      this.manualPHF.offsetA.value as number,
      this.manualPHF.offsetB.value as number,
      this.manualPHF.offsetC.value as number,
      this.manualPHF.offsetD.value as number,
    );

    this.newPHCalibration.notes = this.f.noteField.value as string;
  }

  setCurrentPHFormValues(deviceId: number): void {
    const bufferIds = this.collectPHBufferIds();
    this.newPHCalibration = new PhCalibration(
      deviceId,
      this.f.calibrationName.value as string,
      this.f.saveLocation.value as string,
      false, // Control has been hidden and is always false per Ogi request
      this.pHf.temperature.value as number,
      bufferIds,
    );

    this.newPHCalibration.notes = this.f.noteField.value as string;
  }

  setCurrentManualSpargingFormValues(deviceId: number): void {
    this.newSpargingCalibration = new SpargingCalibration(
      deviceId,
      this.f.calibrationName.value as string,
      this.f.saveLocation.value as string,
      null,
      this.manualSpargingF.slopeA.value as number,
      this.manualSpargingF.slopeB.value as number,
      this.manualSpargingF.slopeC.value as number,
      this.manualSpargingF.slopeD.value as number,
      this.manualSpargingF.interceptA.value as number,
      this.manualSpargingF.interceptB.value as number,
      this.manualSpargingF.interceptC.value as number,
      this.manualSpargingF.interceptD.value as number,
    );

    this.newSpargingCalibration.notes = this.f.noteField.value as string;
  }

  setCurrentSpargingFormValues(deviceId: number): void {
    this.newSpargingCalibration = new SpargingCalibration(
      deviceId,
      this.f.calibrationName.value as string,
      this.f.saveLocation.value as string,
      this.spargingF.volume.value as number,
    );

    this.newSpargingCalibration.notes = this.f.noteField.value as string;
  }

  ngOnDestroy(): void {
    // Cancel any existing device API calls
    if (this.deviceSubscription) {
      this.deviceSubscription.unsubscribe();
    }

    if (this.setupSubscription) {
      this.setupSubscription.unsubscribe();
    }

    if (this.calibrationTypeSubscription) {
      this.calibrationTypeSubscription.unsubscribe();
    }

    if (this.deviceSelectionSubscription) {
      this.deviceSelectionSubscription.unsubscribe();
    }

    this.temperatureControlSubscriptions.forEach((subscription) => {
      subscription.unsubscribe();
    });

    this.pHBufferSubscriptions.forEach((subscription) => {
      subscription.unsubscribe();
    });

    // Cache form if we're being destroyed due to 401 navigation
    if (
      this.authEventService.isUnauthorizedNavigationPending &&
      !this.calibrationForm.pristine
    ) {
      const formValues = this.getCurrentFormValues();
      this.calibrationService.onUpdateCreateCalibrationSetup(
        this.f.setup.value,
      );
      this.calibrationService.onUpdateCreateCalibrationFormCache(formValues);
    }

    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  canExit(): Promise<boolean> | boolean {
    return this.isNavigationAllowed();
  }

  private isNavigationAllowed(): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      if (
        this.calibrationForm.pristine ||
        !this.currentUser ||
        this.authEventService.isUnauthorizedNavigationPending
      ) {
        resolve(true);
      } else {
        this.openModal('attention');
        this.allowNavigation.pipe(take(1)).subscribe((isConfirmed) => resolve(isConfirmed));
      }
    });
  }
}
